import { createAsyncThunk } from "@reduxjs/toolkit";

import * as wishlistsAPI from "../../../api/wishlists";
import { IBasket } from "../../../models/catalogue";
import { loadBasket } from "./loadBasket";

/**
 * Arguments for removeWishlistLine thunk.
 */
interface RemoveWishlistLineArgs {
    lineURL: string;
}

/**
 * Remove a line from a wishlist.
 *
 * This thunk removes a product from a wishlist (e.g., "Previously in Basket")
 * and reloads the basket to refresh the wishlist state. Typically used when
 * moving items from wishlist back to basket or permanently removing them.
 *
 * @param {string} args.lineURL - API URL of the wishlist line to remove
 * @returns {Promise<IBasket>} Promise resolving to the reloaded basket
 *
 * @example
 * // Remove item from wishlist
 * dispatch(removeWishlistLine({ lineURL: wishlistLine.url }));
 *
 * @sideEffects
 * - Makes API call to remove wishlist line via wishlistsAPI.removeLine
 * - Dispatches loadBasket thunk to reload basket and wishlist data
 * - Updates `data.basket` and `data.wishlists` with reloaded data via extraReducers
 */
export const removeWishlistLine = createAsyncThunk(
    "checkout/removeWishlistLine",
    async (args: RemoveWishlistLineArgs, { dispatch }): Promise<IBasket> => {
        await wishlistsAPI.removeLine(args.lineURL);
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
